<template>
  <form class="variant-options-container" id ="variant-options-container" @submit.prevent>
    <div class="form-input-container" v-if="product">
      <div class="has-options-heading" :class="brand.name" v-if="hasOptions">{{ brand.service.variantSelectionHeading }}</div>
      <div v-for="(option, index) in product.options" :key="index" class="variant-summary" :class="brand.name">
        <ButtonList
          v-if="optionDisplayTypeOrdered(option, index) === 'button'"
          required
          :tabindex="index"
          :brand="brand"
          :variant="variant"
          :optionLabel="option.label"
          :selectField="selectedOption(option.label)"
          :optionList="filteredOptions(option.values, index)"
          :inputClass="inputClass(index, option.label)"
          :errors="variantErrors(index, option)"
          v-on:click="resetAndSelectValue('button', option.label, $event.target.id, index)">
        </ButtonList>
        <DropDown class="variant-option-dropdown" :class="option.label"
          v-if="optionDisplayTypeOrdered(option, index) === 'dropdown'"
          required
          :tabindex="index"
          :selectOptions="filteredOptions(option.values, index)"
          :brand="brand"
          :label="option.label"
          :selectField="selectedOption(option.label)"
          :inputClass="inputClass(index, option.label)"
          :errors="variantErrors(index, option)"
          v-on:selected="resetAndSelectValue('dropdown', option.label, $event.target.value, index)">
        </DropDown>
        <DropDown class="variant-option-dropdown"
          v-if="optionDisplayTypeOrdered(option, index) === 'yearDropdown'"
          required
          :tabindex="index"
          :selectOptions="getYearArray"
          :brand="brand"
          :label="option.label"
          :sorted=false
          :selectField="selectedOption(option.label)"
          :inputClass="inputClass(index, option.label)"
          :errors="variantErrors(index, option)"
          v-on:selected="resetAndSelectValue('yearDropdown', option.label, $event.target.value, index)">
        </DropDown>
        <NumericInput
          v-if="optionDisplayTypeOrdered(option, index) === 'numericInput'"
          required
          :tabindex="index"
          :brand="brand"
          :defaultNumber="selectedOption(option.label)"
          :label="option.label"
          :inputClass="inputClass(index, option.label)"
          :errors="variantErrors(index, option)"
          v-on:changeNumber="resetAndSelectValue('numericInput', option.label, $event.target.value, index)"
          v-on:enter="handleSubmit">
        </NumericInput>
        <RadioButton
          v-if="optionDisplayTypeOrdered(option, index) === 'radioButton'"
          required
          :tabindex="index"
          :brand="brand"
          :label="option.label"
          :optionList="option.values"
          :defaultValue="selectedOption(option.label)"
          :inputClass="inputClass(index, option.label)"
          :errors="variantErrors(index, option)"
          v-on:input="resetAndSelectValue('radioButton', option.label, $event.target.value, index)">
        </RadioButton>
      </div>
      <div v-show="!showCallDealershipMessage" class="price-container" :class="brand.name">
        <div class="retail-price-heading">{{ calculateRetailAmount() }}</div>
        <div class="price-heading">{{ calculateAmount() }}</div>
        <div class="price-discount-heading">{{ discountTag() }}</div>
      </div>
      <div class="alert" v-show="showCallDealershipMessage">
        <p>We are unable to find an Online Offer for the selected Vehicle Options.</p>
        <p>Please contact <b>{{ dealer.name }}</b> to learn more about offers for your vehicle at
        <a v-bind:href="`tel:${bdcPhoneCompact}`" v-on:click="trackBdcPhone">{{ dealer.phone.bdc | phoneFormatter }}</a>.</p>
      </div>
    </div>
    <div class="button-container" :class="brand.name">
      <div class="error-message" v-if="v$.$error">Provide the required information to Add To Cart.</div>
      <button class="ss-button show-product-button" id="show-product-button" :class="brand.name" v-on:click="handleSubmit" type="button" :disabled="showCallDealershipMessage">
        <span id="spinnerSubmit" class="spinner-border" role="status" aria-hidden="true" v-show="apiProcessing">
        </span>
        <i class="fa fa-shopping-cart"></i>
        Add To Cart
      </button>
      <div v-if="productType==='bundle'" class="disclaimer" :class="brand.name" v-html="product.disclaimer"></div>
    </div>
  </form>
</template>

<script>
import DropDown from '@/components/shared/DropDown'
import ButtonList from '@/components/shared/ButtonList'
import NumericInput from '@/components/shared/NumericInput'
import RadioButton from '@/components/shared/RadioButton'
import Tracking from '@/mixins/tracking-mixin'
import { ImageModel } from '@/models/image-model'
import { EntityParser } from '@/utils/entity-parser'
import { useBreadcrumbService } from '@/services/breadcrumb-service'
import { useEshopService } from '@/services/eshop-service'
import { mapState, mapGetters } from 'vuex'
import { BOOLEAN_VARIANTS, NUMERIC_VARIANTS, EshopModel } from '@/models/eshop-model'
import { required, helpers } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'

export default {
  name: 'VariantSelection',
  mixins: [Tracking],
  props: {
    brand: Object,
    dealer: Object,
    product: Object,
    customer: Object,
    productType: String,
    productArray: Array,
    step: Number,
    category: String
  },
  data: function () {
    const componentData = {}
    return this.dataReset(componentData)
  },
  watch: {
    product: 'validateProduct',
    showCallDealershipMessage: 'logFailedInteraction'
  },
  created () {
    this.fetchData()
  },
  setup () {
    return {
      v$: useVuelidate()
    }
  },
  validations () {
    return {
      variant: {
        $each: helpers.forEach({
          input: {
            required: helpers.withMessage('Make a selection', required)
          }
        })
      }
    }
  },
  components: {
    DropDown,
    ButtonList,
    NumericInput,
    RadioButton
  },
  computed: {
    ...mapState({
      vehicle: state => state.vehicle
    }),
    ...mapGetters({
      getCartItem: ['cart/getItemById']
    }),
    getYearArray () {
      return EntityParser.getArrayOfYears()
    },
    maxSavings () {
      const savings = this.product.max_spread && this.product.max_spread.retail_amount && this.product.max_spread.amount ? +this.product.max_spread.retail_amount - +this.product.max_spread.amount : 0
      return savings
    }
  },
  methods: {
    calculateAmount () {
      return this.price.amount ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(this.price.amount) : ''
    },
    calculateRetailAmount () {
      return +this.price.retail_amount > +this.price.amount ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(this.price.retail_amount) : ''
    },
    changeAvailableOptions (currentIndex) {
      // As the user makes selections filter the next option by what is available
      // Only attempt to filter options if there are visible options to filters
      let filteredOptions = []
      const variantCollection = this.getCollection(currentIndex)
      // 1. walk the branches and create a list of valid options for the next variant
      variantCollection.forEach(variantValue => {
        const matchBranch = this.traverseOption(this.product.variants, variantValue, 0, currentIndex)
        filteredOptions = filteredOptions.concat(matchBranch)
      })
      // 2. Set the currentOption to distinct list of filtered options
      if (filteredOptions.length > 0) {
        const distinctOptions = Array.from(new Set(filteredOptions))
        const nextOption = this.product.options[currentIndex + 1]
        if (nextOption && nextOption.label) {
          this.currentOptions[nextOption.label] = distinctOptions
        }
        return true // valid options exist continue
      } else {
        return false // no valid options proceed to sad path
      }
    },
    getCollection (index) {
      // Custom numeric options must be calculated from the user input.
      // variant.value is temporary and changes from an array to a specific value
      let variantCollection = []
      if (this.variant[index]) {
        // For Boolean we check every value
        if (this.variant[index].value && this.variant[index].value.length > 0 && this.variant[index].value.length <= 2 && Array.isArray(this.variant[index].value) && this.variant[index].value.every(element => { return BOOLEAN_VARIANTS.includes(element.toLowerCase()) })) {
          const matchedOption = this.product.options.filter(item => item.label.toLowerCase() === this.variant[index].label.toLowerCase())[0].values
          variantCollection = EshopModel.matchBooleanOption(this.variant[index].input, matchedOption)
        // For numeric one of the reserved words needs to be in the label
        } else if (this.variant[index].label.split(' ').some(element => { return NUMERIC_VARIANTS.includes(element.toLowerCase()) })) {
          const matchedOption = this.product.options.filter(item => item.label.toLowerCase() === this.variant[index].label.toLowerCase())[0].values
          variantCollection = EshopModel.matchNumericOption(this.variant[index].input, matchedOption)
        } else {
          variantCollection.push(this.variant[index].input)
        }
      }
      return variantCollection
    },
    inputClass (index, label) {
      if (this.v$.$errors[0] && this.v$.$errors[0].$message[index][0]) {
        return label + ' is-invalid'
      } else {
        return label
      }
    },
    variantErrors (index, option) {
      const variantError = []
      if (this.v$.$errors[0] && this.v$.$errors[0].$message[index][0]) {
        if (this.optionDisplayComponent(option) === 'radioButton') {
          variantError.push('Make a selection')
        } else if (this.optionDisplayComponent(option) === 'numericInput') {
          variantError.push('Enter required value')
        } else {
          variantError.push(this.v$.$errors[0].$message[index][0])
        }
      }
      return variantError
    },
    traverseOption (branch, currentVariant, currentIndex, selectedIndex) {
      // recursive function to find the set of valid options as the user selects an option
      if (currentIndex === selectedIndex) {
        const matchBranch = Object.entries(branch).filter((item) => item.includes(currentVariant))
        if (matchBranch.length > 0) {
          const returnKeys = Object.keys(matchBranch[0][1])
          return returnKeys
        }
      } else if (currentIndex < selectedIndex) {
        const iterator = this.getCollection(currentIndex)
        let matchedBranch = []
        for (const item of iterator) {
          const nextBranch = branch[item]
          if (nextBranch) {
            const matchedOptions = this.traverseOption(nextBranch, currentVariant, currentIndex + 1, selectedIndex)
            // matched options contain what matched in a single branch add it to the option collection
            if (matchedOptions) {
              matchedBranch = matchedBranch.concat(matchedOptions)
            }
          }
        }
        // Matched branch contains all the options from the different branches that matched
        if (matchedBranch) {
          return matchedBranch
        }
      }
    },
    filteredOptions (defaultOptions, index) {
      // If we have a filtered list of options use it if not default to all the product options
      const filterLabel = this.product.options[index].label
      // if (this.variant[index].label.toLowerCase().includes('year') || this.variant[index].label.toLowerCase().includes('mileage') || this.variant[index].label.toLowerCase().includes('months')) {
      if (this.currentOptions && this.product.options &&
        filterLabel &&
        this.currentOptions[filterLabel] &&
        this.currentOptions[filterLabel].length > 0) {
        return this.currentOptions[filterLabel]
      } else {
        return defaultOptions
      }
    },
    matchVariantBranch (index) {
      // Match the selected variants to the available options
      let currentVariant = []
      if (this.product.options && this.variant.length === this.product.options.length &&
        this.variant[0].value && this.variant[0].value.length > 0) {
        this.price = {}
        currentVariant = this.getCollection(0)
        const matchedVariant = this.traverseNode(this.product.variants, currentVariant, 0, index)
        if (matchedVariant) {
          this.variant[0].value = matchedVariant
          this.visibleOptionIndex = this.variant.length
        }
      }
    },
    traverseNode (branch, currentVariant, currentIndex, selectedIndex) {
      // recursive function to walk the variant tree with customer selection
      // and find a matching product
      let next = []
      currentIndex += 1

      // With type discovery the values can be arrays or single values make them all arrays
      let iterator = []
      if (Array.isArray(currentVariant)) {
        iterator = currentVariant
      } else {
        iterator.push(currentVariant)
      }
      for (const item of iterator) {
        const nextBranch = branch[item]
        if (nextBranch) {
          // Continue traversal only if there are more variants to traverse
          if (this.variant.length > currentIndex) {
            next = this.getCollection(currentIndex)
            const matchedVariant = this.traverseNode(nextBranch, next, currentIndex, selectedIndex)
            if (matchedVariant) {
              this.variant[currentIndex - 1].value = item
              return item // return the variant value we matched
            }
          } else if (nextBranch && nextBranch.amount) {
            // We matched selections to a valid price node
            this.showCallDealershipMessage = false
            this.setPrices(nextBranch)
            return item // this is the last variant value we matched
          }
        }
      }
    },
    dataReset: function (src) {
      const { optionDisplayComponent } = useEshopService()
      const { setSteps } = useBreadcrumbService()
      src.apiProcessing = false
      src.hasOptions = false
      src.showCallDealershipMessage = false
      src.price = {}
      src.visibleOptionIndex = 0
      src.currentOptions = {}
      src.variant = [{ input: '', label: '', value: [] }]
      src.setSteps = setSteps
      src.optionDisplayComponent = optionDisplayComponent
      return src
    },
    discountTag () {
      const discountTag = this.price.discount_amount ? `Save ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(this.price.discount_amount)} (${Number(this.price.discount_percent).toLocaleString('en-US', { style: 'percent', minimumFractionDigits: 0 })})` : ''
      return discountTag
    },
    logFailedInteraction () {
      // log any interactions that display the call dealership message
      if (this.showCallDealershipMessage) {
        this.$store.commit('product/setProduct', { result: this.productPayload() })
        this.$store.dispatch('session/logInteraction', {
          interaction_type: 'eshop',
          action: 'call_dealership',
          metadata: this.$store.getters['product/getProductInteractionData']
        })
      }
    },
    optionDisplayTypeOrdered (option, index) {
      if (index > this.visibleOptionIndex) {
        return '' // don't display option until the options before it are completed
      } else {
        return this.optionDisplayComponent(option)
      }
    },
    productPayload: function () {
      // Capture the details of the product selected for purchase set to CamelCase for store
      const payload = {}
      payload.code = this.productType === 'product' ? this.product.product_id : this.product.bundle_id
      payload.title = this.product.title
      payload.discountUnit = this.product.discount_unit
      payload.discountValue = this.price.amount
      payload.retailValue = this.calculateRetailAmount
      payload.promotionTag = this.calculateAmount
      payload.variant = this.variant
      return payload
    },
    reset (index) {
      // 1. Reset what is visible
      if (index === 0) { // reset the entire form if 1st element changes
        document.getElementById('variant-options-container').reset()
      }

      // 2. clear the selected variant
      for (let i = index; i < this.variant.length; i++) {
        this.variant.splice(i, 1, { label: this.variant[i].label, value: '', input: '' })
      }
      // 3. Reset the variants selected
      this.resetVisibleOptions(index)
      this.price = {}
    },
    resetSelectedValue (selectLabel, index) {
      this.price = {}
      this.visibleOptionIndex = index // don't show next options if selected variant evaluates to nothing
      const selected = this.variant.find(({ label }) => label === selectLabel)
      // If we find the record then clear the selected formatting
      if (selected) {
        const currentIndex = this.variant.indexOf(selected)
        // Sad path clear out the remainin variants
        for (let i = currentIndex; i < this.variant.length; i++) {
          this.variant.splice(i, 1, { label: this.variant[i].label, value: '', input: '' }) // clear the selected values
        }
      }
      this.showCallDealershipMessage = true
    },
    resetAndSelectValue (listType, selectLabel, eventValue, index) {
      // for buttons we have to reset before we select to account for a user changing the option they selected
      this.reset(index)
      this.selectVariantValue(listType, selectLabel, eventValue, index)
    },
    selectedOption (selectLabel) {
      // if the user goes back to the page then reset to the last set of selected values
      // match the selected variant
      let matchedOption = ''
      if (this.variant) {
        if (Object.keys(this.variant).length !== 0) {
          const match = this.variant.find(({ label }) => label.toLowerCase() === selectLabel.toLowerCase())
          if (match) {
            matchedOption = match.input
          }
        }
      }
      return matchedOption
    },
    selectVariantValue (listType, selectLabel, eventValue, index) {
      // if they change a selection then hide error message
      this.showCallDealershipMessage = false
      this.v$.$reset()
      const matchedOption = this.product.options.filter(item => item.label === selectLabel)[0].values
      let variantValue
      if (listType === 'radioButton') {
        variantValue = EshopModel.matchBooleanOption(eventValue, matchedOption)
      } else if (listType === 'yearDropdown' || listType === 'numericInput') {
        variantValue = EshopModel.matchNumericOption(eventValue, matchedOption)
      } else {
        variantValue = eventValue
      }

      if (variantValue && variantValue.length > 0) {
        this.showCallDealershipMessage = false
        this.storeVariantValue(listType, selectLabel, variantValue, eventValue, index)
        if (this.visibleOptionIndex < this.product.options.length - 1) {
          this.visibleOptionIndex += 1
        } else {
          if (this.visibleOptionIndex > index + 1) {
            this.visibleOptionIndex = index + 1
          }
        }
      } else {
        this.resetSelectedValue(selectLabel, index)
      }
    },
    // This same calculation is done when we load cart from local storage in initializeEShopCart
    setPrices (priceTreeNode) {
      this.price.retail_amount = priceTreeNode.retail_amount
      this.price.amount = priceTreeNode.bundle_amount ? priceTreeNode.bundle_amount : priceTreeNode.amount
      this.price.option_image = priceTreeNode.option_image ? priceTreeNode.option_image : ''
      // Only if the retail_amount is higher than the currrent price
      if (+this.price.amount < +priceTreeNode.retail_amount) {
        this.price.discount_amount = +priceTreeNode.retail_amount - +this.price.amount
        this.price.discount_percent = (+this.price.discount_amount / +priceTreeNode.retail_amount)
      }
      if (this.price.option_image) {
        this.$emit('image_change', this.price.option_image)
      } else {
        this.$emit('image_change', '')
      }
    },
    storeVariantValue (listType, selectLabel, variantValue, eventValue, index) {
      let matchedBranch
      const selected = this.variant.find(({ label }) => label.toLowerCase() === selectLabel.toLowerCase())
      if (selected) {
        this.variant.splice(this.variant.indexOf(selected), 1, { label: selectLabel, value: variantValue, input: eventValue })
      } else {
        this.variant.push({ label: selectLabel, value: variantValue, input: eventValue })
      }
      this.matchVariantBranch(index)
      if (this.product && this.product.options &&
      this.visibleOptionIndex <= this.product.options.length) {
        matchedBranch = this.changeAvailableOptions(index)
        if (!matchedBranch) {
          this.showCallDealershipMessage = true
          this.visibleOptionIndex -= 1
        }
      }
    },
    validateProduct () {
      if (Object.prototype.hasOwnProperty.call(this.product, 'options')) {
        this.hasOptions = true
      } else { // Product has no options so get the set Price
        this.hasOptions = false
        this.setPrices(this.product)
      }
    },
    resetVisibleOptions (index) {
      if (this.visibleOptionIndex === this.product.options.length) {
        this.visibleOptionIndex -= 1
      } else {
        this.visibleOptionIndex = index
      }
    },
    async handleSubmit () {
      if (this.hasOptions) {
        this.v$.$touch() // trigger input validation
      }
      if (!this.v$.$error) {
        this.matchVariantBranch(0)
        if (!this.showCallDealershipMessage) {
          this.apiProcessing = true
          this.$store.dispatch('track/gaEcommCartEvent', {
            vm: this,
            event: 'add_to_cart',
            brand: this.brand.name,
            currency: 'USD',
            value: this.price.amount,
            items: [{
              code: this.productType === 'product' ? this.product.product_id : this.product.bundle_id,
              title: this.product.title,
              discount_unit: this.product.discount_unit,
              brand: this.brand.name,
              price: this.price.amount
            }]
          })
          await this.$store.dispatch('cart/addToEShopCart',
            {
              show: false,
              purchaseState: 'review',
              id: this.productType === 'product' ? this.product.product_id : this.product.bundle_id,
              imageUrl: ImageModel.makeProductImageUrl({ brandName: this.brand.name, imageName: this.product.image_name }),
              title: this.product.title,
              description: this.product.description,
              unit: this.product.discount_unit,
              discount_value: this.price.amount,
              retail_value: this.price.retail_amount,
              options: this.variant,
              category: this.product.category,
              productType: this.productType,
              productArray: this.productArray
            }
          )
          this.$store.commit('product/setProduct', { result: this.productPayload() })
          this.setSteps({ currentStep: 3, category: this.category })
          this.$store.dispatch('session/logInteraction', { interaction_type: 'eshop', action: 'add_to_cart', metadata: this.$store.getters['product/getProductInteractionData'] })
          this.$store.dispatch('track/facebookEvent', { event: 'Add-To-Cart' })
          this.$nextTick(() => { this.$router.replace({ name: 'cart' }) })
          this.apiProcessing = false
        } else {
          this.$store.dispatch('session/logInteraction', { interaction_type: 'eshop', action: 'call_dealership', metadata: { vehicle: this.vehicle, variant: this.variant } })
        }
      }
    },
    async fetchData () {
      this.validateProduct()
      // If the product being viewed is in the cart. Default the variants to the users selections
      if (this.$route && this.$route.params && this.$route.params.id) {
        const cartItem = this.getCartItem(this.$route.params.id)
        // 1. Load cart settings if they exist
        if (cartItem.length > 0) {
          this.variant = EshopModel.selectVariantsWithCart(this.product.options, cartItem[0].options, this.vehicle).variant
        } else {
          // 2. default to values from customer vehicle
          await this.$store.dispatch('customer/getDetails')
          // Default options to known vehicle is available
          this.variant = EshopModel.selectVariantsWithKnownVehicle(this.product.options, this.vehicle)
        }
        if (this.hasOptions) {
          this.matchVariantBranch(0)
          // if the 1st variant is prepopulated and there is no matching product then set sad path
          if (this.variant[0].value && this.variant[0].value.length === 0 &&
            this.variant[0].input && this.variant[0].input.length > 0) {
            this.showCallDealershipMessage = true
          }
          // walk all the options and filter available options
          let matchedBranch = true
          for (let i = 0; i < this.product.options.length - 1; i++) {
            if (matchedBranch) {
              matchedBranch = this.changeAvailableOptions(i)
            }
          }
        }
      }
    }
  }
}
</script>

<style scoped>
.has-options-heading {
  margin: 20px 0px;
  font-weight: bold;
}
.variant-options-container {
  display: flex;
  flex-direction: column;
  text-align: left;
  background-color: #ffffff;
  padding: 5px;
  width: 100%; /* sets form size */
}
.variant-summary {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.variant-option-dropdown {
  width: 100%;
  margin: 10px 0px;
}
.price-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-content: center;
  text-align: center;
  align-items: center;
}
.retail-price-heading {
  margin: 20px 0px 0px;
  text-align: left;
  align-self: left;
  font-size: 1em;
  font-weight: bold;
  text-decoration: line-through;
}
.price-heading {
  margin: 20px 0px 0px;
  text-align: left;
  align-self: left;
  font-size: 2em;
  font-weight: bold;
  color: var(--variant-color)
}
.price-discount-heading {
  margin: 0px 0px 20px;
  text-align: left;
  align-self: left;
  font-size: 1em;
  color: red;
}
.vehicle-info-container {
  padding: 10px;
  width: 100%; /* defers form size to maintenance-overlay width */
}
.form-input-container {
  display: flex;
  flex-direction: column;
  justify-content: left;
  text-align: left;
  align-items: left;
  align-content: left;
}
.button-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  row-gap: 20px;
  text-align: center;
  align-items: center;
  align-content: center;
  margin: 30px 10px 20px;
}
.button-container.bmw,
.price-container.bmw,
.button-container.mini,
.price-container.mini {
  justify-content: flex-start;
  align-content: flex-start;
  text-align: left;
  align-items: flex-start;
}
.button-container.bmw,
.button-container.mini {
  margin: 30px 0px;
}
.schedule-hdr {
  display: inline-block;
  justify-content: center;
  text-align: center;
  width: 100%;
}
.description {
  font-size: 1.2rem;
  text-align: center;
}
.mobile-button {
  font-size: 0.75rem;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin-top: 20px;
}
.selected {
  background-Color: var(--variant-color)
}
.alert {
  padding-top: 20px;
}
.disclaimer {
  margin-top: 20px;
  font-size: 0.5rem;
  text-align: center;
}
.disclaimer.bmw,
.disclaimer.mini {
  text-align: left;
}
.error-message {
  color: var(--error-color);
}
/* styles for different screen widths follow */
@media only screen and (min-width: 576px) {
}
@media only screen and (min-width: 768px) {
}
@media only screen and (min-width: 992px) {
}
@media only screen and (min-width: 1200px) {
}
</style>
